import AutocompleteService from '@/services/autocomplete';
import { ALL_MEDICATIONS, MEDICATION_CATEGORIES } from './__fixtures__/discoverFixtures';
// Uncomment along with the requests below.
// import store from '@/store';
// import { Request } from '@/lib/http';
// import AuthEnum from '../lib/enums/auth-enum';

// MedicationService
//
// Medication-domain reference data, loaded once when the user enters the
// Formulary Management module: the full medication list and medication
// categories.
//
// The medication list reuses the app's EXISTING per-pump-model autocomplete
// rather than the `GET /medications` endpoint DISCOVER_DATA_CONTRACTS.md §3/§9
// originally planned — see getAllMedications below.
//
// TEMPORARY: `/medication-categories` does not exist here yet (it is a real
// endpoint in the source project but has no counterpart in this repo), so it
// resolves the placeholder payload from `__fixtures__/discoverFixtures` in the
// endpoint's documented response shape. The real call is commented above the
// return — uncomment the request plus the imports above, delete the fixture
// return. See DISCOVER_DATA_CONTRACTS.md §4.
//
// Endpoints are tenant-scoped, matching every other tenant service here (see
// services/intelligence.js): the blueprint mounts at `/api/formulary`, the
// tenant id is the first path segment, and `authType: AuthEnum.TENANT` sends the
// tenant token. On the Flask side `@auth.tenant_route` prefixes `/<tenant_id>`
// and validates it against the token, so the id is required — not optional.

// Uncomment along with the requests below.
// const baseURL = '/api/formulary';

// The autocomplete subjects backing the medication list.
//
// Formulary Management spans pump models, so it unions the per-model subjects
// rather than picking one. These are the same subjects the Safety dashboards'
// medication filters use (see dashboards/models.yaml), which is the point: FM
// resolves medication names from the same place the rest of the app does.
//
// NOT the untenanted `drug_name` subject. That one resolves to the shared
// `bainbridge` Looker model (autocomplete/commands.py falls back to
// LOOKER_MODEL when the subject has no `t_` prefix and no `::`), which is a
// DIFFERENT dataset from the tenant's pump data — it would look populated while
// disagreeing with every Safety dashboard.
//
// EVERY pump model with a `drug_name` subject is listed, not just the ones a
// given tenant runs. Most tenants use a subset, and the extras cost one request
// each that returns []: the backend logs `incorrect tenant model` and returns
// an empty list for a model missing from the tenant's `allowed_models` rather
// than erroring. Listing them all is what keeps FM correct on any tenant —
// omitting a model would show an empty medication list here while the Safety
// dashboards show data, which is the exact divergence this change exists to
// fix. Keep in sync with `filters/autocomplete.yaml`.
//
// FUTURE: if Formulary Management gets its own Looker model, this collapses
// back to a single subject and the union below can go away.
const MEDICATION_AUTOCOMPLETE_SUBJECTS = [
  't_alaris::drug_name',
  't_baxter::drug_name',
  't_bbraun::drug_name',
  't_medfusion::drug_name',
];

export default {
  // -> { medications: string[] }
  //
  // Reuses the same autocomplete the Safety FilterBar medication filter is
  // driven by (`/api/autocomplete/<tenant_id>/<subject>`), so Formulary
  // Management and the rest of the app resolve medication names from the same
  // tenant-scoped source instead of two divergent lists.
  //
  // Each subject returns a flat `string[]` when no query params are sent: the
  // mapped-object response shape is only produced when a `mapped` param is
  // present (see bbh/autocomplete/commands.py fetch_autocomplete_subject_results),
  // and we deliberately send none — we want the unfaceted list.
  //
  // The per-model requests are independent: one rejecting must not lose the
  // other's medications, so each falls back to [] and the union is built from
  // whatever came back.
  //
  // De-duplication is CASE-INSENSITIVE. A medication stocked on both pump
  // models appears in both lists, and the two models do not agree on casing
  // ("FENTANYL" vs "Fentanyl"), which an exact-match Set would keep as two
  // entries — they then render identically once SharedFilterBar runs them
  // through capitalize(), so the dropdown would show visible duplicates. First
  // spelling seen wins; the result is sorted case-insensitively so the order
  // matches how the names are displayed, then wrapped in the documented
  // `{ medications }` envelope so the store contract is unchanged.
  getAllMedications() {
    const requests = MEDICATION_AUTOCOMPLETE_SUBJECTS.map((subject) =>
      AutocompleteService.getAutocompleteResults(subject, {}).catch(() => [])
    );
    return Promise.all(requests).then((responses) => {
      const byLowerName = new Map();
      responses
        .filter(Array.isArray)
        .flat()
        .forEach((name) => {
          if (typeof name !== 'string' || !name) return;
          const key = name.toLowerCase();
          if (!byLowerName.has(key)) byLowerName.set(key, name);
        });
      const medications = [...byLowerName.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

      // DEV/LOCAL ONLY. The autocomplete service cannot run locally at all — it
      // needs Firestore, GCS, Looker and BigQuery (see autocomplete/tenant.py),
      // so both subjects come back empty and every medication input in the
      // module is unusable. Fall back to the fixture so the pages stay
      // testable.
      //
      // Deliberately gated on a build-time flag rather than "did the request
      // come back empty", so a genuinely empty tenant in a deployed environment
      // shows NOTHING instead of plausible-looking fake drug names. The flag is
      // false in every deployed build, so this branch and the fixture import
      // are stripped at build time.
      if (FIXTURE_FALLBACK_ENABLED && !medications.length) {
        return { medications: [...ALL_MEDICATIONS] };
      }

      return { medications };
    });
  },

  // -> { medicationCategories: Category[] }
  getMedicationCategories() {
    // const tenantId = store.getters.tenant.id;
    // const request = new Request(`${baseURL}/${tenantId}/medication-categories`, {
    //   authType: AuthEnum.TENANT,
    // });
    // return request.send();
    return Promise.resolve({ medicationCategories: MEDICATION_CATEGORIES });
  },
};
